const SELECTOR = {
  CONTAINER: '.product-personalisation',
  INPUT: '.field__input',
  FIELD: '.product-personalisation__field',
  COUNTER: '[data-personalisation-counter]',
};

elabs.ProductPersonalisation = class {
  constructor(element) {
    this.view = element;
    this.inputs = Array.from(this.view.querySelectorAll(SELECTOR.INPUT));
    this.form = this.inputs[0]?.form || null;
    this.wireEvents();
    this.inputs.forEach((input) => this.updateCounter(input));
  }

  wireEvents = () => {
    this.inputs.forEach((input) => {
      input.addEventListener('input', this.handleInput);
    });

    // The product forms use "novalidate" and submit via fetch, so native "required"
    // validation never runs. We listen on the document in the capture phase so this
    // runs before the theme's own submit handler and can block an invalid submission.
    // The sticky buy bar has its own form that doesn't contain the fields, so on that
    // form we also mirror the values into it before it is serialized.
    if (this.form) {
      document.addEventListener('submit', this.handleFormSubmit, true);
    }
  };

  handleFormSubmit = (event) => {
    const submittedForm = event.target;
    if (!this.form || !(submittedForm instanceof HTMLFormElement)) {
      return;
    }

    // Use getAttribute('id') rather than .id: the product form contains an
    // <input name="id">, which clobbers the form's .id property with that element.
    const mainFormId = this.form.getAttribute('id');
    const submittedFormId = submittedForm.getAttribute('id');
    const isMainForm = submittedFormId === mainFormId;
    const isBuyBarForm = submittedFormId === `${mainFormId}-product-buy-bar`;
    if (!isMainForm && !isBuyBarForm) {
      return;
    }

    const invalidInput = this.inputs.find((input) => !input.checkValidity());
    if (invalidInput) {
      event.preventDefault();
      event.stopImmediatePropagation();
      invalidInput.reportValidity();
      invalidInput.focus();
      return;
    }

    if (isBuyBarForm) {
      this.syncValuesToBuyBar(submittedForm);
    }
  };

  syncValuesToBuyBar = (buyBarForm) => {
    this.inputs.forEach((input) => {
      const name = input.getAttribute('name');
      const selector = `input[data-personalisation-mirror][name="${CSS.escape(name)}"]`;
      let mirror = buyBarForm.querySelector(selector);

      if (input.value) {
        if (!mirror) {
          mirror = document.createElement('input');
          mirror.type = 'hidden';
          mirror.setAttribute('data-personalisation-mirror', '');
          mirror.setAttribute('name', name);
          buyBarForm.appendChild(mirror);
        }
        mirror.value = input.value;
      } else if (mirror) {
        mirror.remove();
      }
    });
  };

  handleInput = (event) => {
    const input = event.target;
    let value = input.value;

    if (input.dataset.noEmoji === 'true') {
      value = this.stripEmojis(value);
    }

    if (input.dataset.allowedPattern) {
      value = this.enforcePattern(input, value);
    }

    if (value !== input.value) {
      input.value = value;
    }

    this.updateCounter(input);
  };

  stripEmojis = (value) => {
    try {
      return value.replace(/\p{Extended_Pictographic}/gu, '');
    } catch {
      return value.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{1F1E6}-\u{1F1FF}]/gu, '');
    }
  };

  enforcePattern = (input, value) => {
    const pattern = this.getPattern(input);
    if (!pattern) {
      return value;
    }

    if (pattern.test(value)) {
      input.dataset.lastValidValue = value;
      return value;
    }

    return input.dataset.lastValidValue || '';
  };

  getPattern = (input) => {
    if (input.compiledPattern !== undefined) {
      return input.compiledPattern;
    }

    try {
      input.compiledPattern = new RegExp(input.dataset.allowedPattern);
    } catch {
      input.compiledPattern = null;
    }

    return input.compiledPattern;
  };

  updateCounter = (input) => {
    const counter = input.closest(SELECTOR.FIELD)?.querySelector(SELECTOR.COUNTER);
    if (!counter) {
      return;
    }

    const template = counter.dataset.template;
    if (!template) {
      return;
    }

    const max = counter.dataset.max;
    counter.textContent = template.replace('[used]', input.value.length.toString()).replace('[max]', max);
  };
};

const initProductPersonalisation = () => {
  document.querySelectorAll(SELECTOR.CONTAINER).forEach((element) => {
    if (element.dataset.initialized === 'true') {
      return;
    }
    element.dataset.initialized = 'true';
    void new elabs.ProductPersonalisation(element);
  });
};

initProductPersonalisation();

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initProductPersonalisation);
}
